import {
    CreateButton,
    DataTable,
    DateField,
    ExportButton,
    List,
    ReferenceField,
    SearchInput,
    SelectInput,
    TextField,
    ListPagination,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Calendar, Radio } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { TopToolbar } from '../layout/TopToolbar';

const SourceBadge = () => {
    const record = useRecordContext();
    if (!record?.source) return <span className="text-muted-foreground text-xs">—</span>;
    const colors: Record<string, string> = {
        Website: 'bg-info/10 text-info border-info/30',
        Email: 'bg-purple-50 text-purple-700 border-purple-200',
        Phone: 'bg-success/10 text-success border-success/30',
        Reference: 'bg-warning/10 text-warning border-warning/30',
        'Cold Call': 'bg-muted text-muted-foreground border-border',
    };
    return (
        <Badge variant="outline" className={`text-[10px] font-semibold ${colors[record.source] || 'bg-muted text-muted-foreground border-border'}`}>
            <Radio className="w-3 h-3 mr-1" />
            {record.source}
        </Badge>
    );
};

const StatusCell = () => {
    const record = useRecordContext();
    if (!record) return null;
    return <StatusBadge status={record.status} />;
};

const ScoreCell = () => {
    const record = useRecordContext();
    const score = record?.qualification_score || 0;
    const color = score > 70 ? 'text-success' : score > 40 ? 'text-warning' : 'text-muted-foreground';
    return <span className={`font-bold text-sm ${color}`}>{score > 0 ? `${score}%` : '—'}</span>;
};

const EnquiryListActions = () => (
    <TopToolbar>
        <ExportButton className="bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary/90 border border-primary/20 hover:border-primary/30 transition-colors h-9 rounded-lg font-semibold cursor-pointer dark:bg-teal-950/30 dark:text-teal-300 dark:border-teal-900/50 dark:hover:bg-teal-900/40" />
        <CreateButton label="New Enquiry" />
    </TopToolbar>
);

const filters = [
    <SearchInput key="q" source="q" alwaysOn />,
    <SelectInput
        key="status"
        source="status"
        label="Pipeline Stage"
        emptyText="All Stages"
        choices={[
            { id: 'new', name: 'New' },
            { id: 'in_discussion', name: 'In Discussion' },
            { id: 'qualified', name: 'Qualified' },
            { id: 'disqualified', name: 'Disqualified' },
            { id: 'converted', name: 'Converted' },
            { id: 'closed', name: 'Closed' },
        ]}
    />,
];

export const EnquiryList = () => (
    <List
        title="Enquiries"
        actions={<EnquiryListActions />}
        filters={filters}
        sort={{ field: 'created_at', order: 'DESC' }}
        pagination={null} // Disable default external pagination
        className="p-6"
    >
        <Card className="border border-border rounded-xl bg-card shadow-sm overflow-hidden flex flex-col mb-4">
            <div className="flex-1 overflow-x-auto scrollbar-hide">
                <DataTable rowClick="show">
                    <DataTable.Col
                        source="reference"
                        label="Enquiry No."
                        className="font-mono font-bold text-primary"
                    />
                    <DataTable.Col label="Company">
                        <ReferenceField source="company_id" reference="companies" link={false}>
                            <TextField source="name" className="font-semibold text-foreground" />
                        </ReferenceField>
                    </DataTable.Col>
                    <DataTable.Col label="Contact">
                        <ReferenceField source="contact_id" reference="contacts" link={false}>
                            <span className="text-sm text-muted-foreground">
                                <TextField source="first_name" /> <TextField source="last_name" />
                            </span>
                        </ReferenceField>
                    </DataTable.Col>
                    <DataTable.Col label="Assigned">
                        <ReferenceField source="assigned_to" reference="sales" link={false}>
                            <TextField source="first_name" className="text-xs font-medium text-muted-foreground" />
                        </ReferenceField>
                    </DataTable.Col>
                    <DataTable.Col label="Source">
                        <SourceBadge />
                    </DataTable.Col>
                    <DataTable.Col label="Score">
                        <ScoreCell />
                    </DataTable.Col>
                    <DataTable.Col label="Status">
                        <StatusCell />
                    </DataTable.Col>
                    <DataTable.Col label="Received">
                        <div className="flex items-center gap-2 text-muted-foreground text-xs">
                            <Calendar className="w-3 h-3" />
                            <DateField source="created_at" />
                        </div>
                    </DataTable.Col>
                </DataTable>
            </div>
            {/* Professional docked rectangular pagination footer */}
            <div className="bg-muted/20 border-t border-border py-2.5 px-4 shrink-0">
                <ListPagination />
            </div>
        </Card>
    </List>
);
